import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ruler, ShoppingBag, Star, Truck, X, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const MEASUREMENTS = {
  "0-3M": { chest: '16" / 41cm', length: '12" / 30cm', height: "50–62cm" },
  "3-6M": { chest: '17" / 43cm', length: '13" / 33cm', height: "62–68cm" },
  "6-9M": { chest: '17.5" / 44cm', length: '14" / 36cm', height: "68–74cm" },
  "9-12M": { chest: '18" / 46cm', length: '15" / 38cm', height: "74–80cm" },
  "12-18M": { chest: '19" / 48cm', length: '16" / 41cm', height: "80–86cm" },
  "18-24M": { chest: '20" / 51cm', length: '17" / 43cm', height: "86–92cm" },
  "1-2Y": { chest: '20" / 51cm', length: '17" / 43cm', height: "80–92cm" },
  "2-3Y": { chest: '21" / 53cm', length: '19" / 48cm', height: "92–98cm" },
  "3-4Y": { chest: '22" / 56cm', length: '21" / 53cm', height: "98–104cm" },
  "4-5Y": { chest: '23" / 58cm', length: '22" / 56cm', height: "104–110cm" },
};

export default function ProductModal() {
  const { selectedProduct: p, setSelectedProduct, addToCart, setCartOpen, setCheckoutOpen } = useStore();
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showMeasure, setShowMeasure] = useState(false);
  const [pincode, setPincode] = useState("");
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (p) {
      setSize(null);
      setImgIdx(0);
      setEta(null);
      setPincode("");
      setShowMeasure(false);
      api.get(`/products/${p.id}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {});
    }
  }, [p]);

  const close = () => setSelectedProduct(null);

  const checkEta = () => {
    if (!/^\d{6}$/.test(pincode)) {
      toast.error("Enter a valid 6-digit pincode");
      return;
    }
    const days = 2 + (parseInt(pincode[0], 10) % 4);
    const date = new Date(Date.now() + days * 86400000);
    setEta(`Delivery by ${date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} — ${days}–${days + 1} days to ${pincode}`);
  };

  const requireSize = () => {
    if (!size) {
      toast.error("Please pick a size first");
      return false;
    }
    return true;
  };

  return (
    <AnimatePresence>
      {p && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={close}
          data-testid="product-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-cream w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] grid md:grid-cols-2"
          >
            <div className="relative flex flex-col">
              <img src={p.images[imgIdx] || p.images[0]} alt={p.name} className="w-full h-64 md:h-auto md:flex-1 object-cover md:rounded-tl-[2rem]" data-testid="product-modal-image" />
              {p.images.length > 1 && (
                <div className="flex gap-2 p-3 bg-white/70 md:rounded-bl-[2rem]" data-testid="product-modal-thumbnails">
                  {p.images.map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      onClick={() => setImgIdx(i)}
                      className={`rounded-lg overflow-hidden border-2 transition-all duration-300 ${i === imgIdx ? "border-terracotta" : "border-transparent opacity-60 hover:opacity-100"}`}
                      data-testid={`product-modal-thumb-${i}`}
                      aria-label={`Photo ${i + 1}`}
                    >
                      <img src={url} alt={`${p.name} photo ${i + 1}`} className="w-14 h-16 object-cover" />
                    </button>
                  ))}
                </div>
              )}
              {p.badge && (
                <span className="absolute top-4 left-4 bg-ink text-cream font-mono text-[10px] tracking-widest uppercase rounded-full px-3 py-1.5">{p.badge}</span>
              )}
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sage-dark">{p.category}</p>
                  <h3 className="font-serif font-black text-2xl sm:text-3xl text-ink mt-1" data-testid="product-modal-name">{p.name}</h3>
                </div>
                <button onClick={close} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="product-modal-close" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {p.rating && (
                <p className="flex items-center gap-1.5 mt-2 font-mono text-xs text-charcoal">
                  <Star className="w-4 h-4 fill-gold text-gold" /> {p.rating} · {p.review_count} parent reviews
                </p>
              )}
              <div className="flex items-baseline gap-3 mt-4">
                <span className="font-serif font-bold text-3xl text-ink">{inr(p.price)}</span>
                {p.mrp > p.price && <span className="text-charcoal/50 line-through">{inr(p.mrp)}</span>}
              </div>
              <p className="text-sm text-charcoal leading-relaxed mt-4">{p.description}</p>
              <p className="font-mono text-[10px] tracking-widest uppercase text-sage-dark mt-3">{p.fabric} · {p.colors.join(" / ")}</p>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-charcoal/70">Select size</p>
                  <button onClick={() => setShowMeasure(!showMeasure)} className="flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase text-terracotta" data-testid="product-modal-measure-toggle">
                    <Ruler className="w-3.5 h-3.5" /> Measurements
                  </button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="product-modal-sizes">
                  {p.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`rounded-full px-4 py-2 font-mono text-xs border transition-all duration-300 ${
                        size === s ? "bg-ink text-cream border-ink" : "border-ink/15 hover:border-terracotta"
                      }`}
                      data-testid={`size-option-${s.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {showMeasure && (
                  <div className="mt-3 rounded-2xl border border-ink/10 overflow-hidden text-xs" data-testid="product-modal-measurements">
                    <table className="w-full">
                      <thead className="bg-blush font-mono text-[10px] uppercase tracking-widest text-charcoal">
                        <tr><th className="p-2 text-left">Size</th><th className="p-2 text-left">Chest</th><th className="p-2 text-left">Length</th><th className="p-2 text-left">Height</th></tr>
                      </thead>
                      <tbody>
                        {p.sizes.map((s) => (
                          <tr key={s} className="border-t border-ink/5">
                            <td className="p-2 font-mono font-bold">{s}</td>
                            <td className="p-2">{MEASUREMENTS[s]?.chest}</td>
                            <td className="p-2">{MEASUREMENTS[s]?.length}</td>
                            <td className="p-2">{MEASUREMENTS[s]?.height}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mt-5 flex gap-2">
                <input
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Pincode"
                  className="w-32 rounded-full border border-ink/10 bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta"
                  data-testid="product-modal-pincode-input"
                />
                <button onClick={checkEta} className="rounded-full border border-ink/15 px-4 py-2.5 text-xs font-semibold hover:border-terracotta hover:text-terracotta transition-colors" data-testid="product-modal-eta-btn">
                  <Truck className="w-3.5 h-3.5 inline mr-1" /> Check delivery
                </button>
              </div>
              {eta && <p className="mt-2 text-xs text-sage-dark font-medium" data-testid="product-modal-eta-result">{eta}</p>}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => { if (requireSize()) { addToCart(p, size); setCartOpen(true); close(); } }}
                  className="flex-1 rounded-full border-2 border-ink text-ink py-3.5 text-sm font-semibold hover:bg-ink hover:text-cream transition-colors duration-300 flex items-center justify-center gap-2"
                  data-testid="product-modal-add-to-cart"
                >
                  <ShoppingBag className="w-4 h-4" /> Add to Basket
                </button>
                <button
                  onClick={() => { if (requireSize()) { addToCart(p, size); setCheckoutOpen(true); close(); } }}
                  className="flex-1 rounded-full bg-terracotta text-white py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors duration-300 flex items-center justify-center gap-2"
                  data-testid="product-modal-buy-now"
                >
                  <Zap className="w-4 h-4" /> Buy Now
                </button>
              </div>

              {reviews.length > 0 && (
                <div className="mt-7 border-t border-ink/10 pt-5" data-testid="product-modal-reviews">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-charcoal/70 mb-3">What parents say</p>
                  <div className="space-y-3 max-h-44 overflow-y-auto pr-1">
                    {reviews.map((r) => (
                      <div key={r.id} className="bg-white rounded-2xl p-3.5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-ink">{r.name} · {r.city}</p>
                          <span className="flex items-center gap-1 font-mono text-[10px]"><Star className="w-3 h-3 fill-gold text-gold" />{r.rating}</span>
                        </div>
                        <p className="text-xs text-charcoal mt-1.5 leading-relaxed">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
